// src/context/ThemeContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { storage } from '../utils/storage';

// Theme configurations
const themes = {
  light: {
    name: 'light',
    colors: {
      primary: '#2563EB', 
      primaryHover: '#1D4ED8',
      secondary: '#10B981', 
      secondaryHover: '#059669',
      accent: '#F59E0B', 
      danger: '#EF4444', 
      warning: '#F59E0B', 
      success: '#10B981', 
      info: '#3B82F6', 
      background: '#FFFFFF',
      surface: '#F9FAFB',
      surfaceHover: '#F3F4F6',
      text: {
        primary: '#111827',
        secondary: '#6B7280',
        tertiary: '#9CA3AF',
        inverse: '#FFFFFF'
      },
      border: '#E5E7EB',
      shadow: 'rgba(0, 0, 0, 0.1)',
      overlay: 'rgba(0, 0, 0, 0.5)'
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    borderRadius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      full: '9999px'
    }
  },
  dark: {
    name: 'dark',
    colors: {
      primary: '#3B82F6', // Blue
      primaryHover: '#2563EB',
      secondary: '#10B981', // Green
      secondaryHover: '#059669',
      accent: '#F59E0B', // Amber
      danger: '#F87171', // Red
      warning: '#FBBF24', // Amber
      success: '#34D399', // Green
      info: '#60A5FA', // Blue
      background: '#111827',
      surface: '#1F2937',
      surfaceHover: '#374151',
      text: {
        primary: '#F9FAFB',
        secondary: '#D1D5DB',
        tertiary: '#9CA3AF',
        inverse: '#111827'
      },
      border: '#374151',
      shadow: 'rgba(0, 0, 0, 0.3)',
      overlay: 'rgba(0, 0, 0, 0.7)'
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    borderRadius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      full: '9999px'
    }
  },
  system: {
    name: 'system',
    followSystem: true
  }
};

// Initial state
const initialState = {
  currentTheme: 'system',
  theme: themes.light,
  isDark: false,
  isSystemDark: false,
  availableThemes: Object.keys(themes),
  fontSize: 'medium',
  fontSizes: {
    small: '14px',
    medium: '16px',
    large: '18px'
  },
  animations: true,
  highContrast: false
};

// Action types
const ThemeActionTypes = {
  SET_THEME: 'SET_THEME',
  TOGGLE_THEME: 'TOGGLE_THEME',
  SET_SYSTEM_THEME: 'SET_SYSTEM_THEME',
  SET_FONT_SIZE: 'SET_FONT_SIZE',
  TOGGLE_ANIMATIONS: 'TOGGLE_ANIMATIONS',
  TOGGLE_HIGH_CONTRAST: 'TOGGLE_HIGH_CONTRAST',
  APPLY_THEME: 'APPLY_THEME'
};

// Reducer
const themeReducer = (state, action) => {
  switch (action.type) {
    case ThemeActionTypes.SET_THEME:
      const selectedTheme = action.payload;
      let appliedTheme;
      let isDark;

      if (selectedTheme === 'system') {
        appliedTheme = state.isSystemDark ? themes.dark : themes.light;
        isDark = state.isSystemDark;
      } else {
        appliedTheme = themes[selectedTheme];
        isDark = selectedTheme === 'dark';
      }

      return {
        ...state,
        currentTheme: selectedTheme,
        theme: appliedTheme,
        isDark
      };

    case ThemeActionTypes.TOGGLE_THEME:
      const newTheme = state.isDark ? 'light' : 'dark';
      return {
        ...state,
        currentTheme: newTheme,
        theme: themes[newTheme],
        isDark: !state.isDark
      };

    case ThemeActionTypes.SET_SYSTEM_THEME:
      const isSystemDark = action.payload;
      let systemTheme = state.theme;
      let systemIsDark = state.isDark;

      if (state.currentTheme === 'system') {
        systemTheme = isSystemDark ? themes.dark : themes.light;
        systemIsDark = isSystemDark;
      }

      return {
        ...state,
        isSystemDark,
        theme: systemTheme,
        isDark: systemIsDark
      };

    case ThemeActionTypes.SET_FONT_SIZE:
      return {
        ...state,
        fontSize: action.payload
      };

    case ThemeActionTypes.TOGGLE_ANIMATIONS:
      return {
        ...state,
        animations: !state.animations
      };

    case ThemeActionTypes.TOGGLE_HIGH_CONTRAST:
      return {
        ...state,
        highContrast: !state.highContrast
      };

    case ThemeActionTypes.APPLY_THEME:
      return {
        ...state,
        theme: { ...state.theme, ...action.payload }
      };

    default:
      return state;
  }
};

// Create context
const ThemeContext = createContext();

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Initialize theme from localStorage and system preference
  useEffect(() => {
    const savedTheme = storage.getItem('theme') || 'system';
    const savedFontSize = storage.getItem('fontSize') || 'medium';
    const savedAnimations = storage.getItem('animations');
    const savedHighContrast = storage.getItem('highContrast');

    // Set font size
    if (savedFontSize) {
      dispatch({
        type: ThemeActionTypes.SET_FONT_SIZE,
        payload: savedFontSize
      });
    }

    // Set animations preference
    if (savedAnimations !== null) {
      dispatch({
        type: ThemeActionTypes.TOGGLE_ANIMATIONS,
        payload: JSON.parse(savedAnimations)
      });
    }

    // Set high contrast preference
    if (savedHighContrast !== null) {
      dispatch({
        type: ThemeActionTypes.TOGGLE_HIGH_CONTRAST,
        payload: JSON.parse(savedHighContrast)
      });
    }

    // Check system theme preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    dispatch({
      type: ThemeActionTypes.SET_SYSTEM_THEME,
      payload: mediaQuery.matches
    });

    // Set initial theme
    dispatch({
      type: ThemeActionTypes.SET_THEME,
      payload: savedTheme
    });

    // Listen for system theme changes
    const handleSystemThemeChange = (e) => {
      dispatch({
        type: ThemeActionTypes.SET_SYSTEM_THEME,
        payload: e.matches
      });
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    const { colors, spacing, borderRadius } = state.theme;

    // Apply CSS custom properties
    if (colors) {
      Object.entries(colors).forEach(([key, value]) => {
        if (typeof value === 'object') {
          Object.entries(value).forEach(([subKey, subValue]) => {
            root.style.setProperty(`--color-${key}-${subKey}`, subValue);
          });
        } else {
          root.style.setProperty(`--color-${key}`, value);
        }
      });
    }

    if (spacing) {
      Object.entries(spacing).forEach(([key, value]) => {
        root.style.setProperty(`--spacing-${key}`, value);
      });
    }

    if (borderRadius) {
      Object.entries(borderRadius).forEach(([key, value]) => {
        root.style.setProperty(`--radius-${key}`, value);
      });
    }

    // Set font size
    root.style.setProperty('--font-size-base', state.fontSizes[state.fontSize]);

    // Apply theme class to body
    document.body.className = `theme-${state.theme.name} ${state.isDark ? 'dark' : 'light'} ${
      state.highContrast ? 'high-contrast' : ''
    } ${state.animations ? 'animations-enabled' : 'animations-disabled'}`;

    // Set theme-color meta tag for mobile browsers
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.content = colors.primary;
    }
  }, [state.theme, state.fontSize, state.animations, state.highContrast, state.isDark]);

  // Set theme
  const setTheme = (themeName) => {
    dispatch({
      type: ThemeActionTypes.SET_THEME,
      payload: themeName
    });
    storage.setItem('theme', themeName);
  };

  // Toggle between light and dark theme
  const toggleTheme = () => {
    dispatch({ type: ThemeActionTypes.TOGGLE_THEME });
    const newTheme = state.isDark ? 'light' : 'dark';
    storage.setItem('theme', newTheme);
  };

  // Set font size
  const setFontSize = (size) => {
    dispatch({
      type: ThemeActionTypes.SET_FONT_SIZE,
      payload: size
    });
    storage.setItem('fontSize', size);
  };

  // Toggle animations
  const toggleAnimations = () => {
    const newAnimations = !state.animations;
    dispatch({ type: ThemeActionTypes.TOGGLE_ANIMATIONS });
    storage.setItem('animations', JSON.stringify(newAnimations));
  };

  // Toggle high contrast
  const toggleHighContrast = () => {
    const newHighContrast = !state.highContrast;
    dispatch({ type: ThemeActionTypes.TOGGLE_HIGH_CONTRAST });
    storage.setItem('highContrast', JSON.stringify(newHighContrast));
  };

  // Get CSS variable value
  const getCSSVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${variable}`);
  };

  // Apply custom theme colors (for advanced customization)
  const applyCustomColors = (colorOverrides) => {
    dispatch({
      type: ThemeActionTypes.APPLY_THEME,
      payload: {
        colors: { ...state.theme.colors, ...colorOverrides }
      }
    });
  };

  // Get current theme colors
  const getThemeColors = () => {
    return state.theme.colors;
  };

  // Check if current theme is dark
  const isCurrentThemeDark = () => {
    return state.isDark;
  };

  // Get theme for specific component (utility function)
  const getComponentTheme = (componentName) => {
    const baseTheme = state.theme;
    
    // Component-specific theme overrides can be added here
    const componentThemes = {
      button: {
        ...baseTheme,
        // Button-specific styling
      },
      card: {
        ...baseTheme,
        // Card-specific styling
      },
      modal: {
        ...baseTheme,
        // Modal-specific styling
      }
    };

    return componentThemes[componentName] || baseTheme;
  };

  const value = {
    ...state,
    setTheme,
    toggleTheme,
    setFontSize,
    toggleAnimations,
    toggleHighContrast,
    getCSSVariable,
    applyCustomColors,
    getThemeColors,
    isCurrentThemeDark,
    getComponentTheme,
    themes: themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;